/**
 * Readers for the brand profile held in `brand.meta`.
 *
 * `meta` is `Record<string, unknown>` on the wire — a jsonb column with no schema behind it — so
 * every field is narrowed here rather than cast at the point of use. A brand seeded by an older
 * script, or edited by hand, can carry anything; the page's job is to render what is genuinely
 * there and show its gap otherwise, never to crash on a shape it did not expect.
 *
 * Written by `scripts/src/demo/seed-brand-profiles.ts`. Kept as plain narrowing functions rather
 * than a Zod schema because the whole object is optional and partially-populated by design: a
 * brand with a tagline and no links is normal, not invalid.
 */

/** A brand's leitmotiv, with whether it is the registered line or derived positioning. */
export interface BrandMetaTagline {
	text: string
	/** `false` = wording derived from on-site claims rather than a registered tagline. */
	verbatim?: boolean
}

/** One external collection a brand's assets live in. */
export interface BrandMetaLink {
	label: string
	/** Null where the destination is unknown — an inert row rather than a fabricated URL. */
	url: string | null
	kind?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value)

/** A non-empty trimmed string, or undefined — the only form worth rendering. */
const asText = (value: unknown): string | undefined => {
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	return trimmed === '' ? undefined : trimmed
}

/**
 * The brand's tagline, or null when it has none.
 *
 * `verbatim` is only carried through when it is genuinely `false`: absent or malformed means
 * "assume the brand's own words", which is the safe reading for a line someone typed by hand.
 */
export const taglineOf = (meta: Record<string, unknown>): BrandMetaTagline | null => {
	const raw = meta.tagline
	// Tolerates the flat-string shape too, so a hand-edited `"tagline": "..."` still renders.
	if (typeof raw === 'string') {
		const text = asText(raw)
		return text ? { text } : null
	}
	if (!isRecord(raw)) return null
	const text = asText(raw.text)
	if (!text) return null
	return raw.verbatim === false ? { text, verbatim: false } : { text }
}

/**
 * The brand's external collections, in the order stored. Empty when it has none.
 *
 * A link with no label is dropped rather than rendered as a blank row: the label is the only
 * part a reader can act on, and `url` is allowed to be null (a known collection whose address
 * is not recorded yet).
 */
export const linksOf = (meta: Record<string, unknown>): BrandMetaLink[] => {
	if (!Array.isArray(meta.links)) return []
	return meta.links.flatMap((entry) => {
		if (!isRecord(entry)) return []
		const label = asText(entry.label)
		if (!label) return []
		return [{ label, url: asText(entry.url) ?? null, ...(asText(entry.kind) ? { kind: asText(entry.kind) } : {}) }]
	})
}

/**
 * A data-quality or brand-safety warning recorded on the brand.
 *
 * Surfaced on the page rather than left in the seed's source document, because the cases it
 * covers are ones a reader acts on — a hijacked brand domain must not be linked from a
 * deliverable, and that is only useful where the brand is being read.
 */
export const cautionOf = (meta: Record<string, unknown>): string | null => asText(meta.caution) ?? null
